import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import TabsManagement from './TabsManagement';
import AppVersionConfig from './AppVersionConfig';
import TechnicalBreakConfig from './TechnicalBreakConfig';
import SupabaseProxyConfig from './SupabaseProxyConfig';
import LegalDocumentsConfig from '../components/LegalDocumentsConfig';
import './Manage.css';

const TAB_TABS = 'tabs';
const TAB_CONTENT = 'content';
const TAB_VERSION = 'version';
const TAB_MAINTENANCE = 'maintenance';
const TAB_PROXY = 'proxy';

export default function Manage() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() => {
    if (tabParam === TAB_CONTENT) return TAB_CONTENT;
    if (tabParam === TAB_VERSION) return TAB_VERSION;
    if (tabParam === TAB_MAINTENANCE) return TAB_MAINTENANCE;
    if (tabParam === TAB_PROXY) return TAB_PROXY;
    return TAB_TABS;
  });
  const [reportsShowContactData, setReportsShowContactData] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);
  const [contentSaving, setContentSaving] = useState(false);

  useEffect(() => {
    const t =
      tabParam === TAB_CONTENT
        ? TAB_CONTENT
        : tabParam === TAB_VERSION
          ? TAB_VERSION
          : tabParam === TAB_MAINTENANCE
            ? TAB_MAINTENANCE
            : tabParam === TAB_PROXY
              ? TAB_PROXY
              : TAB_TABS;
    setActiveTab(t);
  }, [tabParam]);

  const goToTab = (tab) => {
    setActiveTab(tab);
    const search =
      tab === TAB_CONTENT
        ? '?tab=content'
        : tab === TAB_VERSION
          ? '?tab=version'
          : tab === TAB_MAINTENANCE
            ? '?tab=maintenance'
            : tab === TAB_PROXY
              ? '?tab=proxy'
              : '';
    navigate({ pathname: '/manage', search }, { replace: true });
  };

  useEffect(() => {
    if (activeTab !== TAB_CONTENT) return;
    loadContentSettings();
  }, [activeTab]);

  const loadContentSettings = async () => {
    setContentLoading(true);
    try {
      const { data, error } = await supabaseAdmin
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'reportsShowContactData')
        .maybeSingle();

      if (error) throw error;
      const value = data?.setting_value;
      setReportsShowContactData(value !== false);
    } catch (err) {
      console.error('Error loading content settings:', err);
      toast.error('Błąd ładowania ustawień');
      setReportsShowContactData(true);
    } finally {
      setContentLoading(false);
    }
  };

  const handleReportsShowContactDataChange = async (checked) => {
    setContentSaving(true);
    try {
      const { error } = await supabaseAdmin
        .from('app_settings')
        .upsert(
          {
            setting_key: 'reportsShowContactData',
            setting_value: checked,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'setting_key' }
        );

      if (error) throw error;
      setReportsShowContactData(checked);
      toast.success(checked ? 'Sekcja „Dane kontaktowe” włączona' : 'Sekcja „Dane kontaktowe” wyłączona');
    } catch (err) {
      console.error('Error saving content settings:', err);
      toast.error('Błąd zapisu ustawień');
    } finally {
      setContentSaving(false);
    }
  };

  return (
    <div className="manage-page">
      <div className="manage-tabs">
        <button
          type="button"
          className={`manage-tab ${activeTab === TAB_TABS ? 'active' : ''}`}
          onClick={() => goToTab(TAB_TABS)}
        >
          Zarządzanie zakładkami
        </button>
        <button
          type="button"
          className={`manage-tab ${activeTab === TAB_CONTENT ? 'active' : ''}`}
          onClick={() => goToTab(TAB_CONTENT)}
        >
          Zarządzanie treścią
        </button>
        <button
          type="button"
          className={`manage-tab ${activeTab === TAB_VERSION ? 'active' : ''}`}
          onClick={() => goToTab(TAB_VERSION)}
        >
          Wersja aplikacji
        </button>
        <button
          type="button"
          className={`manage-tab ${activeTab === TAB_MAINTENANCE ? 'active' : ''}`}
          onClick={() => goToTab(TAB_MAINTENANCE)}
        >
          Przerwa techniczna
        </button>
        <button
          type="button"
          className={`manage-tab ${activeTab === TAB_PROXY ? 'active' : ''}`}
          onClick={() => goToTab(TAB_PROXY)}
        >
          Włącz/wyłącz proxy
        </button>
      </div>

      {activeTab === TAB_TABS && (
        <div className="manage-tab-panel">
          <TabsManagement />
        </div>
      )}

      {activeTab === TAB_VERSION && (
        <AppVersionConfig />
      )}

      {activeTab === TAB_MAINTENANCE && (
        <TechnicalBreakConfig />
      )}

      {activeTab === TAB_PROXY && (
        <SupabaseProxyConfig />
      )}

      {activeTab === TAB_CONTENT && (
        <div className="manage-tab-panel manage-content-panel">
          <h1 style={{ marginBottom: '24px' }}>Zarządzanie treścią</h1>
          <p style={{ marginBottom: '24px', color: '#666' }}>
            Ustawienia treści wyświetlanej w aplikacji mobilnej.
          </p>
          {contentLoading ? (
            <p>Ładowanie...</p>
          ) : (
            <div
              className="manage-content-card"
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                padding: '20px',
                maxWidth: '560px',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  marginBottom: '8px',
                }}
              >
                <input
                  type="checkbox"
                  checked={reportsShowContactData}
                  disabled={contentSaving}
                  onChange={(e) => handleReportsShowContactDataChange(e.target.checked)}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: '500' }}>
                  Pokaż sekcję „Dane kontaktowe” na ekranie „Nowe zgłoszenie” w aplikacji mobilnej
                </span>
              </label>
              <p style={{ margin: 0, fontSize: '14px', color: '#666', marginLeft: '32px' }}>
                Wyłączenie ukrywa cały kontener z polem telefonu i e-mail na formularzu nowego zgłoszenia.
              </p>
            </div>
          )}

          <LegalDocumentsConfig />
        </div>
      )}
    </div>
  );
}
